/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDInput from "components/MDInput";

// Material-UI components
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import apiClient from "./../../../../apiClient";
import DataTable from "examples/Tables/DataTable";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";

const FetchLocationData = ({ onRefresh, editData }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateInput, setStateInput] = useState("");
  const [districtInput, setDistrictInput] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [locationInput, setLocationInput] = useState("");
  const [statusInput, setStatusInput] = useState("Active");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    onConfirm: null,
  });

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/location");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching location data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [onRefresh]);

  // Handlers
  const handleActivate = async (id, status) => {
    try {
      setConfirmDialog({
        open: true,
        title:
          "Are you sure you want to " +
          (status === "Active" ? "deactivate" : "activate") +
          " this location?",
        onConfirm: async () => {
          try {
            await apiClient.put(`/admin/location/${id}`, { status: status });
            await fetchData(); // Refresh data while staying on the current page
            // Keep current page after refresh
          } catch (error) {
            console.error("Error updating location status:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error activating location:", error);
    }
  };

  const handleDelete = async (id, name) => {
    try {
      setConfirmDialog({
        open: true,
        title: `Are you sure you want to delete the location "${name}"?`,
        onConfirm: async () => {
          try {
            await apiClient.delete(`/admin/location/${id}`);
            fetchData(); // Refresh data after delete
          } catch (error) {
            console.error("Error deleting location:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error deleting location:", error);
    }
  };

  const handleEdit = (id) => {
    // Handle edit functionality
    console.log("Edit state with ID:", id);
    editData(id);
  };

  const handleStateInputChange = (e) => {
    setStateInput(e.target.value);
  };

  const handleDistrictInputChange = (e) => {
    setDistrictInput(e.target.value);
  };

  const handleCityInputChange = (e) => {
    setCityInput(e.target.value);
  };

  const handleLocationInputChange = (e) => {
    setLocationInput(e.target.value);
  };

  const handleStatusInputChange = (e) => {
    setStatusInput(e.target.value);
  };

  const handleFilterSubmit = () => {
    setStateFilter(stateInput);
    setStatusFilter(statusInput);
    setDistrictFilter(districtInput);
    setCityFilter(cityInput);
    setLocationFilter(locationInput);
  };

  // Filter the rows based on search term
  const filteredRows = data.filter((item) => {
    try {
      return (
        (stateFilter === "" ||
          (item.state && item.state.toLowerCase().includes(stateFilter.toLowerCase()))) &&
        (districtFilter === "" ||
          (item.district && item.district.toLowerCase().includes(districtFilter.toLowerCase()))) &&
        (cityFilter === "" ||
          (item.city && item.city.toLowerCase().includes(cityFilter.toLowerCase()))) &&
        (locationFilter === "" ||
          (item.name && item.name.toLowerCase().includes(locationFilter.toLowerCase()))) &&
        (statusFilter === "" ||
          (item.status && item.status.toLowerCase().includes(statusFilter.toLowerCase())))
      );
    } catch (error) {
      console.error("Error filtering row:", error, item);
      return false; // Skip items that cause errors
    }
  });

  const columns = [
    { Header: "State", accessor: "state", width: "15%", align: "left" },
    { Header: "District", accessor: "district", align: "center" },
    { Header: "City", accessor: "city", align: "center" },
    { Header: "Location", accessor: "name", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  const rows = filteredRows.map((item) => ({
    state: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.state}
      </MDTypography>
    ),
    district: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.district}
      </MDTypography>
    ),
    city: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.city}
      </MDTypography>
    ),
    name: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.name}
      </MDTypography>
    ),
    status: (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={item.status}
          color={item.status === "Active" ? "success" : "warning"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    action: (
      <MDBox display="flex" justifyContent="space-around">
        <IconButton onClick={() => handleEdit(item)} color="secondary">
          <EditIcon />
        </IconButton>
        <IconButton
          onClick={() => handleActivate(item._id, item.status)}
          color={item.status === "Active" ? "warning" : "success"}
        >
          <CheckCircleIcon />
        </IconButton>
        <IconButton onClick={() => handleDelete(item._id, item.name)} color="error">
          <DeleteIcon />
        </IconButton>
      </MDBox>
    ),
  }));

  // No data message
  if (loading) return <div>Loading...</div>;
  if (data.length === 0) return <div>No data found.</div>;

  const handleClearFilters = () => {
    setStateFilter("");
    setDistrictFilter("");
    setStatusFilter("");
    setStateInput("");
    setDistrictInput("");
    setCityInput("");
    setCityFilter("");
    setLocationFilter("");
    setLocationInput("");
  };

  return (
    <div>
      <MDBox
        display="flex"
        gap="1rem"
        mb="1rem"
        justifyContent="left"
        alignItems="left"
        ml="2%"
        width="90%"
      >
        <MDInput
          fullWidth
          label="State"
          variant="outlined"
          value={stateInput}
          onChange={handleStateInputChange}
        />
        <MDInput
          fullWidth
          label="District"
          variant="outlined"
          value={districtInput}
          onChange={handleDistrictInputChange}
        />
        <MDInput
          fullWidth
          label="City"
          variant="outlined"
          value={cityInput}
          onChange={handleCityInputChange}
        />
        <MDInput
          fullWidth
          label="Location"
          variant="outlined"
          value={locationInput}
          onChange={handleLocationInputChange}
        />
        <Select
          fullWidth
          variant="outlined"
          value={statusInput}
          onChange={handleStatusInputChange}
          displayEmpty
        >
          <MenuItem value="">
            <em>Status</em>
          </MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </Select>
        <Button
          variant="contained"
          color="primary"
          onClick={handleFilterSubmit}
          sx={{ color: "#fff" }}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleClearFilters}
          sx={{ color: "#fff" }}
        >
          Clear
        </Button>
      </MDBox>
      <Divider />
      <DataTable
        table={{ columns, rows }}
        isSorted={true}
        entriesPerPage={true}
        showTotalEntries={true}
        noEndBorder
      />
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1300,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            borderBottom: `1px solid ${darkMode ? "#333" : "#e0e0e0"}`,
          }}
        >
          {confirmDialog.title}
        </DialogTitle>
        <DialogActions
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            padding: "16px 24px",
          }}
        >
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="inherit"
            sx={{ color: darkMode ? "#ccc" : "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (confirmDialog.onConfirm) {
                await confirmDialog.onConfirm();
              }
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
            sx={{ color: "#fff", ml: 1 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
FetchLocationData.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  editData: PropTypes.func.isRequired,
};

export default FetchLocationData;
